import React, { useMemo, useState, useEffect } from "react";
import TimenTick from "./TimenTick";
import axios from 'axios';
import { IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import toast from "react-hot-toast";

// ── Handover Banner ────────────────────────────────────────────────────────────
// Renders centered inside the chat body when AI transfers to human agent.
// Shows the AI reason (handover_message) and chat context (handover_context)
// as separate labeled sections with a Resolve button.
function HandoverBanner({ message }) {
  const [resolved, setResolved] = useState(false);
  const [resolving, setResolving] = useState(false);

  const handleResolve = async () => {
    if (!message.session_id) return;
    setResolving(true);
    try {
      await axios.post('https://casualfootwears.com/api/resolve_handover/', {
        session_id: String(message.session_id)
      });
      setResolved(true);
      toast.success('Handover resolved ✓', { style: { fontSize: '13px' } });
    } catch (err) {
      console.error('Failed to resolve handover:', err);
      toast.error('Could not resolve — try again', { style: { fontSize: '13px' } });
    } finally {
      setResolving(false);
    }
  };

  if (resolved) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 16px' }}>
        <div style={{
          background: '#e8f5e9', color: '#2e7d32', borderRadius: '8px',
          padding: '6px 16px', fontSize: '0.78rem', fontWeight: '500',
          border: '1px solid #a5d6a7',
        }}>
          ✓ Handover resolved
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '14px 16px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #ff6b35 0%, #f7a07b 100%)',
        color: '#1a1a1a',
        borderRadius: '14px',
        padding: '14px 18px',
        maxWidth: '92%',
        width: '100%',
        fontSize: '0.83rem',
        fontWeight: '500',
        textAlign: 'left',
        boxShadow: '0 3px 10px rgba(255,107,53,0.35)',
        lineHeight: '1.6',
        border: '1px solid rgba(255,107,53,0.4)',
      }}>

        {/* Title row */}
        <div style={{ fontWeight: '700', fontSize: '0.88rem', marginBottom: '10px', textAlign: 'center' }}>
          🚨 AI transferred this chat to human agent
        </div>

        {/* Reason / Message block */}
        {message.handover_message && (
          <div style={{ marginBottom: message.handover_context ? '10px' : '0' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.65, marginBottom: '4px' }}>
              Reason
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.35)',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '0.82rem',
              lineHeight: '1.55',
              whiteSpace: 'pre-wrap',
            }}>
              {message.handover_message}
            </div>
          </div>
        )}

        {/* Context block — only shown if context exists */}
        {message.handover_context && (
          <div style={{ marginBottom: '0' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.65, marginBottom: '4px' }}>
              Chat Context
            </div>
            <div style={{
              background: 'rgba(0,0,0,0.08)',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '0.80rem',
              lineHeight: '1.55',
              whiteSpace: 'pre-wrap',
              fontStyle: 'italic',
            }}>
              {message.handover_context}
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div style={{ fontSize: '0.7rem', opacity: 0.55, marginTop: '10px', marginBottom: '10px', textAlign: 'center' }}>
          {new Date(message.timestamp * 1000).toLocaleString('en-IN', {
            day: 'numeric', month: 'short',
            hour: '2-digit', minute: '2-digit', hour12: true
          })}
        </div>

        {/* Resolve button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleResolve}
            disabled={resolving}
            style={{
              background: resolving ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.75)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '7px 20px',
              fontSize: '0.78rem',
              fontWeight: '600',
              cursor: resolving ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'background 0.2s',
            }}
          >
            <CheckCircleIcon style={{ fontSize: '15px' }} />
            {resolving ? 'Resolving…' : 'Mark as Resolved'}
          </button>
        </div>
      </div>
    </div>
  );
}
// ──────────────────────────────────────────────────────────────────────────────

const getFullUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob:")) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `https://casualfootwears.com${path}`;
};

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Robustly fetches file size via HEAD request.
 * Retries with standard GET if HEAD fails (optional, careful with large files).
 */
const getFileSize = async (url) => {
  if (!url) return null;
  const fullUrl = getFullUrl(url);
  try {
    const response = await axios.head(fullUrl);
    // Normalize headers
    const size = response.headers['content-length'];
    if (size) return formatBytes(parseInt(size, 10));
  } catch (headError) {
    console.warn(`[File Size] HEAD failed for ${url}`, headError);
    // Optional: Could fall back to GET range request if supported, but risky.
  }
  return null;
};

const FileCard = ({ url, filename, onDownload }) => {
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchSize = async () => {
      setLoading(true);
      const s = await getFileSize(url);
      if (mounted) {
        setSize(s);
        setLoading(false);
      }
    };
    fetchSize();
    return () => { mounted = false; };
  }, [url]);

  const getIcon = () => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfIcon style={{ fontSize: 40, color: '#F40F02' }} />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <ImageIcon style={{ fontSize: 40, color: '#4CAF50' }} />;
    return <DescriptionIcon style={{ fontSize: 40, color: '#1976D2' }} />; // Generic doc
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      border: '1px solid rgba(0,0,0,0.1)',
      marginTop: '5px',
      maxWidth: '300px'
    }}>
      <div style={{ marginRight: '10px' }}>
        {getIcon()}
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontWeight: 500, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {filename}
        </div>
        <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
          {loading ? 'Calculating...' : size || 'Unknown Size'} • {filename?.split('.').pop()?.toUpperCase()}
        </div>
      </div>
      <IconButton onClick={onDownload} size="small" style={{ backgroundColor: 'rgba(0,0,0,0.05)', marginLeft: '8px' }}>
        <DownloadIcon />
      </IconButton>
    </div>
  );
};

function Messages({ message }) {
  const [loadIframe, setLoadIframe] = useState(false);
  const [contentType, setContentType] = useState(null); // 'image', 'video', 'audio', 'file', 'iframe'

  // Determine full URL for display/download
  const fullMediaUrl = useMemo(() => getFullUrl(message.media_upload), [message.media_upload]);
  const filename = useMemo(() => message.media_upload?.split("/").pop() || "file", [message.media_upload]);

  // Restored legacy helper for path-based detection if extensions fail
  const getLegacyMediaType = (url) => {
    try {
      const path = new URL(url, 'https://casualfootwears.com').pathname; // Safe URL parsing
      if (path.includes('video') || path.includes('reel')) return 'video';
      if (path.includes('image') || path.includes('photo')) return 'image';
      if (path.includes('audio')) return 'audio';
      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!message.media_upload) {
      setContentType(null);
      return;
    }

    const determineType = async () => {
      let detectedType = null;

      // 0. Pre-check: Extension (Fastest & most reliable for local files)
      const ext = message.media_upload.split('.').pop().split('?')[0].toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) { detectedType = 'image'; }
      else if (['mp4', 'mov', 'webm', 'kv', 'mkv'].includes(ext)) { detectedType = 'video'; }
      else if (['mp3', 'wav', 'ogg', 'opus'].includes(ext)) { detectedType = 'audio'; }

      // 1. META/PLATFORM HANDLING (The user explicitly wanted this checked via headers/hints)
      if (message.platform === 'instagram' || message.platform === 'facebook' || message.media_type === 'ig_reel' || message.media_type === 'share' || message.media_type === 'video') {

        // If extension pre-check didn't find specific media type, OR if it's meta platform, 
        // we try HEAD request to confirm if it's a renderable media or an embeddable page

        try {
          // Try to detect real content type if extension was ambiguous or if it's a meta link
          // This restores the "video handler" logic where we check headers
          const response = await axios.head(fullMediaUrl);
          const type = response.headers['content-type'];

          if (type && type.includes('image')) {
            detectedType = 'image';
          } else if (type && type.includes('video')) {
            detectedType = 'video';
          }
        } catch (e) {
          // CORS or fetch error - fall through to hints
          // console.log("[Media Detect] Head check failed, using hints", e);
        }

        // If still undetermined, use platform hints
        if (!detectedType) {
          if (message.media_type === 'image' || message.media_type === 'share') {
            detectedType = 'image';
          } else if (message.media_type === 'video' || message.media_type === 'ig_reel') {
            // If HEAD failed and it's a reel, it's safer to use Iframe unless it looks like a file
            if (getLegacyMediaType(message.media_upload) === 'video') {
              detectedType = 'video';
            } else {
              detectedType = 'iframe';
            }
          } else {
            detectedType = 'iframe';
          }
        }
      }

      // 2. Fallback to Legacy Path Check if still nothing
      if (!detectedType) {
        detectedType = getLegacyMediaType(message.media_upload);
      }

      // 3. Final Default
      setContentType(detectedType || 'file');
    };

    determineType();
  }, [message.media_upload, message.media_type, message.platform, fullMediaUrl]);

  const handleDownload = async () => {
    try {
      console.log("Downloading from:", fullMediaUrl);
      const response = await axios.get(fullMediaUrl, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: direct simple download attempt
      const link = document.createElement('a');
      link.href = fullMediaUrl;
      link.setAttribute('download', filename);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
  };

  // Special render for AI Handover system messages — centered banner with Resolve button
  if (message.is_handover_system_msg || message.sent_by === 'HANDOVER_SYSTEM') {
    return <HandoverBanner message={message} />;
  }

  return (
    <div
      className={`Chat__Messages msgFrom${message.sent_by === "DJANGO ADMIN" || message.sent_by === "sent" ? "Chat__Reciver" : ""
        } ${message.platform ? message.platform : ""} `}
    >
      <span className={`Chat__MessageContent ${message.platform ? message.platform : ""}`}>

        {/* Text Content */}
        {message.message && message.message.length > 0 && (
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{message.message}</pre>
        )}

        {/* Media Content */}
        {message.media_upload && (
          <div className="media-container" style={{ marginTop: message.message ? '8px' : '0' }}>

            {/* IMAGE */}
            {contentType === 'image' && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={fullMediaUrl}
                  alt="Shared Content"
                  className="Chat__MessageImage"
                  style={{
                    borderRadius: '10px',
                    maxWidth: '100%',
                    display: 'block',
                    // Removed click handler as requested
                    cursor: 'default'
                  }}
                  onError={() => setContentType('file')}
                />
                {/* Download Button Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '5px',
                  right: '5px',
                  background: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  padding: '4px',
                  cursor: 'pointer'
                }} onClick={handleDownload}>
                  <DownloadIcon style={{ color: 'white', fontSize: '1.2rem' }} />
                </div>
              </div>
            )}

            {/* VIDEO */}
            {contentType === 'video' && (
              <div style={{ position: 'relative', maxWidth: '100%' }}>
                <video
                  controls
                  preload="metadata"
                  style={{ maxWidth: '100%', borderRadius: '10px', display: 'block' }}
                  onError={() => setContentType('file')}
                >
                  <source src={fullMediaUrl} />
                  Your browser does not support video.
                </video>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <span onClick={handleDownload} style={{ fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                    <DownloadIcon fontSize="small" style={{ marginRight: '2px' }} /> Download
                  </span>
                </div>
              </div>
            )}

            {/* AUDIO */}
            {contentType === 'audio' && (
              <div style={{ minWidth: '250px' }}>
                <audio controls style={{ width: '100%' }} onError={() => setContentType('file')}>
                  <source src={fullMediaUrl} />
                </audio>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                  <span onClick={handleDownload} style={{ fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                    <DownloadIcon fontSize="small" style={{ marginRight: '2px' }} /> Download
                  </span>
                </div>
              </div>
            )}

            {/* IFRAME (Meta/Legacy Fallback) */}
            {contentType === 'iframe' && (
              <div style={{ width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
                {loadIframe ? (
                  <iframe
                    src={fullMediaUrl}
                    style={{ width: '100%', height: '350px', border: 'none', background: '#000' }}
                    allow="autoplay; encrypted-media"
                    title="Media Content"
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      background: '#000',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'white'
                    }}
                    onClick={() => setLoadIframe(true)}
                  >
                    <div style={{ border: '2px solid white', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                      <VisibilityIcon style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>Tap to View Content</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '5px' }}>
                      (Instagram/Facebook)
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* FILE (Generic Fallback) */}
            {contentType === 'file' && (
              <FileCard url={message.media_upload} filename={filename} onDownload={handleDownload} />
            )}

          </div>
        )}

        {/* Time & Ticks */}
        {(!message.platform || message.platform === 'whatsapp') && <TimenTick message={message} />}
        {(message.platform && message.platform !== 'whatsapp') && (
          <span className="Chat__Time meta-time" style={{ display: 'block', textAlign: 'right', fontSize: '0.7em', marginTop: '4px', opacity: 0.7 }}>
            {(() => {
              const msgDate = new Date(message.timestamp * 1000);
              const today = new Date();
              const isToday = msgDate.getDate() === today.getDate() &&
                msgDate.getMonth() === today.getMonth() &&
                msgDate.getFullYear() === today.getFullYear();
              return isToday
                ? msgDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
                : msgDate.toLocaleString("en-IN", { day: 'numeric', month: 'short', hour: "2-digit", minute: "2-digit", hour12: true });
            })()}
          </span>
        )}
      </span>
    </div>
  );
}

export default Messages;
